import { useMemo, useState } from "react";
import { PROVINCE, norm, findProvincia } from "@/lib/province";

export const Field = ({ label, id, error, children, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    {label && <label htmlFor={id} className="sl-label">{label}</label>}
    {children}
    {error && <p className="sl-error" role="alert" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

export const TextInput = ({ id, value, onChange, error, ...rest }) => (
  <input id={id} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} className="sl-input" data-testid={`${id}-input`} {...rest} />
);

export const Segmented = ({ id, options, value, onChange }) => (
  <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby={id}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        role="radio"
        aria-checked={value === o.value}
        onClick={() => onChange(o.value)}
        className={`seg ${value === o.value ? "seg-on" : ""}`}
        data-testid={`${id}-option-${o.value}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export const ProvinceInput = ({ value, onPick, onType, error }) => {
  const [open, setOpen] = useState(false);
  const list = useMemo(() => {
    const q = norm(value);
    if (!q) return PROVINCE;
    const starts = PROVINCE.filter((p) => norm(p.nome).startsWith(q));
    const has = PROVINCE.filter((p) => !norm(p.nome).startsWith(q) && norm(p.nome).includes(q));
    return [...starts, ...has];
  }, [value]);

  const onChange = (v) => {
    onType(v);
    const m = findProvincia(v);
    if (m) onPick(m);
    setOpen(true);
  };

  return (
    <div className="relative">
      <input
        id="provincia"
        value={value}
        autoComplete="off"
        placeholder="Inizia a scrivere…"
        aria-invalid={!!error}
        aria-autocomplete="list"
        className="sl-input"
        data-testid="provincia-input"
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && list.length > 0 && (
        <ul data-lenis-prevent className="absolute z-30 mt-2 max-h-60 w-full overflow-auto rounded-xl border border-line bg-[#0d0a06] py-1 shadow-2xl" role="listbox" data-testid="provincia-suggestions">
          {list.slice(0, 107).map((p) => (
            <li key={p.nome}>
              <button
                type="button"
                className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm text-cream transition-colors hover:bg-gold/10"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(p);
                  setOpen(false);
                }}
                data-testid={`provincia-option-${norm(p.nome).replace(/[^a-z]/g, "-")}`}
              >
                <span>{p.nome}</span>
                <span className="text-xs text-sand">{p.regione}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const Check = ({ id, checked, onChange, children, error }) => (
  <div className="space-y-2">
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-cream">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-[#c79a4b]" data-testid={`${id}-checkbox`} />
      <span>{children}</span>
    </label>
    {error && <p className="sl-error" role="alert" data-testid={`${id}-error`}>{error}</p>}
  </div>
);
